import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Save, Settings, Wrench, FolderOpen, Calculator, FileUp, Download, Cpu, Circle } from 'lucide-react';
import { IDS } from '@/constants/testIds/wizard';
import { createProject, updateProject } from '@/lib/api';
import { toast } from 'sonner';

export const Topbar = ({
  projectName, setProjectName, currentProjectId,
  gcode, operation, params, globalPost, setGlobalPost,
  onOpenTools, onOpenProjects, onOpenFS, onOpenDxf, onOpenSettings, onOpenImport,
  onSavedProject,
}) => {
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { name: projectName || 'Uj_projekt', operation, params, gcode, notes: '' };
      const saved = currentProjectId
        ? await updateProject(currentProjectId, payload)
        : await createProject(payload);
      onSavedProject?.(saved);
    } catch (e) {
      toast.error('Mentesi hiba: ' + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  return (
    <header className="h-14 flex items-center justify-between px-3 md:px-4 bg-[#0F1214] border-b border-border/70">
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 pr-3 border-r border-border/60">
          <Cpu className="h-5 w-5 text-primary" />
          <div className="flex flex-col leading-tight">
            <span className="text-sm font-semibold">Mach3 NFS Wizard</span>
            <span className="text-[10px] text-muted-foreground uppercase tracking-wider">CAM Varázsló • Magyar</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="status-dot" title="On-line" />
          <Input
            className="h-8 w-56 bg-black/30"
            value={projectName}
            onChange={e => setProjectName(e.target.value)}
            placeholder="Projekt neve"
            data-testid={IDS.topbar.projectName}
          />
          <Button size="sm" variant="secondary" onClick={handleSave} disabled={saving} data-testid={IDS.topbar.save}>
            <Save className="h-4 w-4 mr-1" /> Mentes
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Select value={globalPost?.wcs || 'G54'} onValueChange={v => setGlobalPost({ ...globalPost, wcs: v })}>
          <SelectTrigger className="h-8 w-24 bg-black/30" data-testid={IDS.topbar.wcs}><SelectValue /></SelectTrigger>
          <SelectContent>
            {['G54','G55','G56','G57','G58','G59'].map(w => <SelectItem key={w} value={w}>{w}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={globalPost?.units || 'mm'} onValueChange={v => setGlobalPost({ ...globalPost, units: v })}>
          <SelectTrigger className="h-8 w-24 bg-black/30" data-testid={IDS.topbar.units}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="mm">mm (G21)</SelectItem>
            <SelectItem value="inch">inch (G20)</SelectItem>
          </SelectContent>
        </Select>

        <div className="h-6 w-px bg-border mx-1" />

        <Button size="sm" variant="ghost" onClick={onOpenDxf} data-testid={IDS.topbar.importDxf}>
          <FileUp className="h-4 w-4 mr-1" /> DXF
        </Button>
        <Button size="sm" variant="ghost" onClick={onOpenImport} data-testid={IDS.topbar.importGcode}>
          <Download className="h-4 w-4 mr-1" /> Import G-kód
        </Button>
        <Button size="sm" variant="ghost" onClick={onOpenTools} data-testid={IDS.topbar.tools}>
          <Wrench className="h-4 w-4 mr-1" /> Szerszámok
        </Button>
        <Button size="sm" variant="ghost" onClick={onOpenProjects} data-testid={IDS.topbar.projects}>
          <FolderOpen className="h-4 w-4 mr-1" /> Projektek
        </Button>
        <Button size="sm" variant="ghost" onClick={onOpenFS} data-testid={IDS.topbar.feedspeed}>
          <Calculator className="h-4 w-4 mr-1" /> Feed/Speed
        </Button>
        <Button size="sm" variant="ghost" onClick={onOpenSettings} data-testid={IDS.topbar.settings}>
          <Settings className="h-4 w-4" />
        </Button>
      </div>
    </header>
  );
};

export default Topbar;
